import { QIcon_pause_circle, QIcon_volumne } from '@repo/icons/index'
import { createContext, useContext, useEffect, useState } from 'react'
import apis from '../../../../modules/apis'
import { axiosGet } from '../../../../modules/axiosHelper'
import { DEFAULT_VOICE, RECORDING_STATUS } from '../../../../modules/vars'
import { ttsProvider } from '../../../new-portal/ai-tutor/roleplay/ai'
import LoadingSpinner from '../../LoadingSpinner'
import { stopRecognition } from '../record/recorder'
import SoundController from './soundchunked'

export const SoundContext = createContext<SoundController | null>(null)

export const SoundProvider = ({ children }) => {
  const [currentSound, setCurrentSound] = useState(null)

  useEffect(() => {
    const sound = new SoundController()
    setCurrentSound(sound)
    return () => {
      sound.destroy()
    }
  }, [])

  if (!currentSound) return
  return <SoundContext.Provider value={currentSound}>{children}</SoundContext.Provider>
}

const getSignedUrl = async (audioFile) => {
  if (!audioFile || audioFile === 'streaming') return null
  if (audioFile instanceof Blob) return audioFile
  if (audioFile.startsWith('blob:')) return audioFile
  const thread_id = audioFile.split('/').slice(-2)[0]
  const filename = audioFile.split('/').slice(-1)[0]
  const [role, idx] = filename.split('.')[0].split('-')
  const res = await axiosGet(apis.conversation.threads.audio(thread_id, role, idx))
  return res.url
}

export default function Audio({
  dialog,
  isLast,
  recordingStatus,
  setRecordingStatus,
  isPopup,
  isStreamingPlaying,
  isStreamingLoading,
  isStreamingLoadingData,
  setIsStreamingLoadingData,
  stopStreamingSound,
  isSummary,
  isRecording,
}) {
  const sound = useContext(SoundContext)
  const [isPlaying, setIsPlaying] = useState(false)
  const [loading, setLoading] = useState(false)

  const playTTS = () =>
    ttsProvider(
      sound,
      () => null,
      () => null,
      () => null,
      DEFAULT_VOICE,
      ''
    ).then((tts) => {
      tts.update(dialog.content, () => {
        setLoading(false)
      })
      tts.finalize().then((buffers) => {
        setIsPlaying(false)
      })
    })

  const playAudioFiles = () => {
    stopRecognition(false)
    setLoading(true)
    sound.stop().then(async () => {
      setIsPlaying(true)
      setRecordingStatus(RECORDING_STATUS.BEFORE_LISTENING)
      Promise.all(dialog.audio_files.map(getSignedUrl))
        .then((urls) => urls.filter((x) => x))
        .then((signedUrls) => sound.play(signedUrls, () => setLoading(false)))
        .then(([_, buf]) => {
          if (buf !== undefined) setIsPlaying(false)
          else playTTS()
        })
        .catch(playTTS)
    })
  }

  const stopPlaying = () => {
    sound.stop().then(() => setIsPlaying(false))
  }

  const handlePlay = async () => {
    if (isRecording) return
    if (isStreamingLoading) return
    if (isStreamingPlaying) {
      await stopStreamingSound()
    }
    setLoading(true)
    stopRecognition(false)
    await sound.stop()
    await new Promise<void>((r) => {
      const intv = setInterval(() => {
        if (!isStreamingLoadingData) {
          clearInterval(intv)
          r()
        }
      }, 100)
    })
    playAudioFiles()
  }

  const handleStop = () => {
    stopPlaying()
    setRecordingStatus(RECORDING_STATUS.END_LISTENING)
  }

  useEffect(() => {
    if (recordingStatus == RECORDING_STATUS.START_RECORDING) handleStop()
    if (isPopup) handleStop()
    if (isSummary) handleStop()
  }, [recordingStatus, isPopup, isSummary])

  if (!dialog.audio_files?.length) {
    return <div className="w-[10px]" />
  }

  if (isLast && (isStreamingPlaying || isStreamingLoading)) {
    return (
      <>
        {(isStreamingPlaying && loading) || isStreamingLoading ? (
          <div></div>
        ) : (
          <div onClick={stopStreamingSound}>
            <QIcon_pause_circle width={20} height={20} />
          </div>
        )}
      </>
    )
  }

  return (
    <>
      {isPlaying ? (
        loading ? (
          <div>
            <LoadingSpinner sm show primary />
          </div>
        ) : (
          <div onClick={handleStop}>
            <QIcon_pause_circle width={20} height={20} />
          </div>
        )
      ) : (
        <div onClick={handlePlay}>
          <QIcon_volumne width={20} height={20} />
        </div>
      )}
    </>
  )
}
